import React, { useState, useEffect, useRef } from "react";
import {
  Users,
  Database,
  Settings,
  Ban,
  Check,
  X,
  Key,
  Edit3,
  Crown,
  UserMinus,
  Undo,
} from "lucide-react";

const truncatePasskey = (value, maxLen = 34) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}...`;
};

const AdminPanel = ({ telegramId, onClose, onOpenProfile }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [selectedUser, setSelectedUser] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [passkeyText, setPasskeyText] = useState("");

  // Состояние для поиска
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Состояние для фильтров
  const [accessFilter, setAccessFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [gsTokensFilter, setGsTokensFilter] = useState("all");
  const [passkeyFilter, setPasskeyFilter] = useState("all");
  const [downloadsFilter, setDownloadsFilter] = useState("all");
  const [passkeyOptions, setPasskeyOptions] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [showBulkPasskeyModal, setShowBulkPasskeyModal] = useState(false);
  const [bulkPasskeyText, setBulkPasskeyText] = useState("");
  const [bulkPasskeySubmitting, setBulkPasskeySubmitting] = useState(false);
  const selectAllCheckboxRef = useRef(null);

  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Состояние для глобального поиска
  const [tokenInfo, setTokenInfo] = useState(null);
  const [checkingToken, setCheckingToken] = useState(false);
  const [globalSearchEnabled, setGlobalSearchEnabled] = useState(true);
  const [updatingGlobalSearch, setUpdatingGlobalSearch] = useState(false);

  // Состояние для настройки проверки трекеров
  const [trackerCheckEnabled, setTrackerCheckEnabled] = useState(true);
  const [updatingTrackerCheck, setUpdatingTrackerCheck] = useState(false);

  // Состояние для режима оплаты: subscription (ON) / lifetime (OFF)
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(true);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);

  // Авто-назначение personal passkey новым покупателям
  const [autoAssignPasskey, setAutoAssignPasskey] = useState(false);
  const [updatingAutoAssignPasskey, setUpdatingAutoAssignPasskey] =
    useState(false);
  const [passkeyFileConfigured, setPasskeyFileConfigured] = useState(null);

  // Состояние для модального окна выдачи доступа (grant access)
  const [grantModalUser, setGrantModalUser] = useState(null);
  const [grantDuration, setGrantDuration] = useState("lifetime"); // "lifetime" | "30" | "90" | ... | "custom"
  const [grantCustomDays, setGrantCustomDays] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [grantSubmitting, setGrantSubmitting] = useState(false);

  // Состояние для модального окна с IP адресами
  const [selectedUserIps, setSelectedUserIps] = useState(null);
  const [blockedIps, setBlockedIps] = useState([]);
  const [ipBlockValue, setIpBlockValue] = useState("");
  const [ipBlockReason, setIpBlockReason] = useState("");
  const [loadingIpBlocks, setLoadingIpBlocks] = useState(false);
  const [updatingIpBlocks, setUpdatingIpBlocks] = useState(false);

  // Загрузка данных
  useEffect(() => {
    loadData();
    loadGlobalSearchSetting();
    loadTrackerCheckSetting();
    loadSubscriptionSetting();
    loadAutoAssignPasskeySetting();
    loadIpBlocks();
  }, []);
  const loadData = async (
    search = "",
    searchType = "",
    filters = {},
    page = 1,
  ) => {
    try {
      setLoading(true);

      const headers = {
        "Content-Type": "application/json",
        Authorization: `tma ${window.Telegram.WebApp.initData}`,
      };

      // Формируем URL с параметрами поиска и фильтрами
      let usersUrl = `/api/admin/users`;
      const queryParams = [];

      // Добавляем пагинацию
      queryParams.push(`page=${page}`);
      queryParams.push(`limit=100`);

      if (search && searchType) {
        queryParams.push(`search=${encodeURIComponent(search)}`);
        queryParams.push(`searchType=${searchType}`);
      }

      // Добавляем фильтры
      const currentAccessFilter = filters.accessFilter || accessFilter;
      const currentStatusFilter = filters.statusFilter || statusFilter;
      const currentGsTokensFilter = filters.gsTokensFilter || gsTokensFilter;
      const currentPasskeyFilter = filters.passkeyFilter || passkeyFilter;
      const currentDownloadsFilter =
        filters.downloadsFilter || downloadsFilter;

      if (currentAccessFilter && currentAccessFilter !== "all") {
        queryParams.push(`accessFilter=${currentAccessFilter}`);
      }
      if (currentStatusFilter && currentStatusFilter !== "all") {
        queryParams.push(`statusFilter=${currentStatusFilter}`);
      }
      if (currentGsTokensFilter && currentGsTokensFilter !== "all") {
        queryParams.push(`gsTokensFilter=${currentGsTokensFilter}`);
      }
      if (currentPasskeyFilter && currentPasskeyFilter !== "all") {
        queryParams.push(
          `passkeyFilter=${encodeURIComponent(currentPasskeyFilter)}`,
        );
      }
      if (currentDownloadsFilter && currentDownloadsFilter !== "all") {
        queryParams.push(`downloadsFilter=${currentDownloadsFilter}`);
      }

      if (queryParams.length > 0) {
        usersUrl += `?${queryParams.join("&")}`;
      }

      const usersResponse = await fetch(usersUrl, { headers });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users);
        setPasskeyOptions(usersData.passkeys || []);
        if (usersData.pagination) {
          setCurrentPage(usersData.pagination.currentPage);
          setTotalPages(usersData.pagination.totalPages);
          setTotalUsers(usersData.pagination.totalUsers);
        }
      }

      // Загружаем статистику
      const statsResponse = await fetch(`/api/admin/stats`, { headers });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error loading admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadData(
        searchTerm,
        searchType,
        {
          accessFilter,
          statusFilter,
          gsTokensFilter,
          passkeyFilter,
          downloadsFilter,
        },
        newPage,
      );
    }
  };

  const handleSearch = async (search, type) => {
    setIsSearching(true);
    setSearchTerm(search);
    setSearchType(type);
    setCurrentPage(1);
    await loadData(search, type, {}, 1);
    setIsSearching(false);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchType("");
    setCurrentPage(1);
    loadData("", "", {}, 1);
  };

  // Функция для применения фильтров
  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      accessFilter: filterType === "access" ? value : accessFilter,
      statusFilter: filterType === "status" ? value : statusFilter,
      gsTokensFilter: filterType === "gsTokens" ? value : gsTokensFilter,
      passkeyFilter: filterType === "passkey" ? value : passkeyFilter,
      downloadsFilter: filterType === "downloads" ? value : downloadsFilter,
    };

    if (filterType === "access") setAccessFilter(value);
    if (filterType === "status") setStatusFilter(value);
    if (filterType === "gsTokens") setGsTokensFilter(value);
    if (filterType === "passkey") setPasskeyFilter(value);
    if (filterType === "downloads") setDownloadsFilter(value);

    setCurrentPage(1);
    loadData(searchTerm, searchType, newFilters, 1);
  };

  // Функция для сброса всех фильтров
  const resetFilters = () => {
    setAccessFilter("all");
    setStatusFilter("all");
    setGsTokensFilter("all");
    setPasskeyFilter("all");
    setDownloadsFilter("all");
    setSelectedUserIds(new Set());
    setShowBulkPasskeyModal(false);
    setBulkPasskeyText("");
    setSearchTerm("");
    setSearchType("");
    setCurrentPage(1);
    loadData(
      "",
      "",
      {
        accessFilter: "all",
        statusFilter: "all",
        gsTokensFilter: "all",
        passkeyFilter: "all",
        downloadsFilter: "all",
      },
      1,
    );
  };

  // Функция для закрытия панели с сбросом фильтров
  const handleClose = () => {
    resetFilters();
    onClose();
  };

  const pageUserIds = users.map((user) => String(user.telegram_id));
  const allPageSelected =
    pageUserIds.length > 0 &&
    pageUserIds.every((id) => selectedUserIds.has(id));
  const somePageSelected = pageUserIds.some((id) => selectedUserIds.has(id));

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate =
        somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const toggleUserSelection = (telegramId) => {
    const id = String(telegramId);
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageUserIds.forEach((id) => next.delete(id));
      } else {
        pageUserIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkPasskeySubmit = async ({ clear = false } = {}) => {
    const passkey = clear ? "" : bulkPasskeyText.trim();
    if ((!clear && !passkey) || selectedUserIds.size === 0 || bulkPasskeySubmitting) {
      return;
    }

    if (clear) {
      const confirmed = window.confirm(
        `Clear personal passkeys for ${selectedUserIds.size} selected user${
          selectedUserIds.size === 1 ? "" : "s"
        }?`,
      );
      if (!confirmed) return;
    }

    setBulkPasskeySubmitting(true);
    try {
      const response = await fetch("/api/admin/users/bulk-passkey", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({
          telegram_ids: Array.from(selectedUserIds),
          passkey,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to update passkeys");
      }

      setShowBulkPasskeyModal(false);
      setBulkPasskeyText("");
      setSelectedUserIds(new Set());
      loadData(
        searchTerm,
        searchType,
        {
          accessFilter,
          statusFilter,
          gsTokensFilter,
          passkeyFilter,
          downloadsFilter,
        },
        currentPage,
      );
    } catch (error) {
      console.error("Error bulk updating passkeys:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setBulkPasskeySubmitting(false);
    }
  };

  const handleUserAction = async (action, userId, data = {}) => {
    // Для возврата средств попросим подтверждение
    if (action === "refund-stars") {
      if (
        !window.confirm(
          "Are you sure you want to refund the latest stars payment for this user? This will also revoke their access.",
        )
      ) {
        return;
      }
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        if (action === "refund-stars") {
          alert("Stars refunded successfully.");
        }
        loadData(
          searchTerm,
          searchType,
          {
          accessFilter,
          statusFilter,
          gsTokensFilter,
          passkeyFilter,
          downloadsFilter,
        },
          currentPage,
        );
        setSelectedUser(null);
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || "Something went wrong"}`);
      }
    } catch (error) {
      console.error(`Error ${action}:`, error);
      alert(`Error: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Короткая дата DD.MM.YYYY для бейджа подписки
  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("ru-RU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Определяем статус доступа пользователя для бейджа
  const getAccessState = (user) => {
    if (!user.access_granted) return "denied";
    if (!user.premium_expires_at) return "lifetime";
    return new Date(user.premium_expires_at) > new Date()
      ? "subscription"
      : "expired";
  };

  // Открыть модалку выдачи доступа
  const openGrantModal = (user) => {
    setGrantModalUser(user);
    setGrantDuration("lifetime");
    setGrantCustomDays("");
    setGrantNote("");
  };

  // Отправить выдачу доступа (на срок или бессрочно)
  const submitGrantAccess = async () => {
    if (!grantModalUser) return;

    let durationDays = null; // null = lifetime
    if (grantDuration === "custom") {
      const days = parseInt(grantCustomDays, 10);
      if (!Number.isFinite(days) || days <= 0) {
        alert("Введите корректное число дней");
        return;
      }
      durationDays = days;
    } else if (grantDuration !== "lifetime") {
      durationDays = parseInt(grantDuration, 10);
    }

    setGrantSubmitting(true);
    try {
      const response = await fetch(
        `/api/admin/users/${grantModalUser.telegram_id}/grant-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram.WebApp.initData}`,
          },
          body: JSON.stringify({
            durationDays,
            note: grantNote || undefined,
          }),
        },
      );

      if (response.ok) {
        setGrantModalUser(null);
        loadData(
          searchTerm,
          searchType,
          {
          accessFilter,
          statusFilter,
          gsTokensFilter,
          passkeyFilter,
          downloadsFilter,
        },
          currentPage,
        );
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.error || "Something went wrong"}`);
      }
    } catch (error) {
      console.error("Error granting access:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setGrantSubmitting(false);
    }
  };

  // Проверка статуса токена глобального поиска
  const checkTokenInfo = async () => {
    setCheckingToken(true);
    try {
      const response = await fetch(`/api/token-info?telegramId=${telegramId}`, {
        headers: {
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
      });
      const data = await response.json();

      if (data.success) {
        setTokenInfo(data.tokenInfo);
      } else {
        setTokenInfo({ error: data.message });
      }
    } catch (error) {
      console.error("Error checking token:", error);
      setTokenInfo({ error: "Failed to check token status" });
    } finally {
      setCheckingToken(false);
    }
  };

  // Загрузка настройки Global Search
  const loadGlobalSearchSetting = async () => {
    try {
      const response = await fetch(`/api/admin/settings/global-search`, {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setGlobalSearchEnabled(data.enabled);
      }
    } catch (error) {
      console.error("Error loading global search setting:", error);
    }
  };

  // Обновление настройки Global Search
  const updateGlobalSearchSetting = async (enabled) => {
    setUpdatingGlobalSearch(true);
    try {
      const response = await fetch(`/api/admin/settings/global-search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();
      if (data.success) {
        setGlobalSearchEnabled(enabled);
      } else {
        alert("Failed to update setting: " + data.error);
      }
    } catch (error) {
      console.error("Error updating global search setting:", error);
      alert("Failed to update setting");
    } finally {
      setUpdatingGlobalSearch(false);
    }
  };

  // Загрузка настройки проверки трекеров
  const loadTrackerCheckSetting = async () => {
    try {
      const response = await fetch(`/api/admin/settings/tracker-check`, {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTrackerCheckEnabled(data.enabled);
      }
    } catch (error) {
      console.error("Error loading tracker check setting:", error);
    }
  };

  // Обновление настройки проверки трекеров
  const updateTrackerCheckSetting = async (enabled) => {
    setUpdatingTrackerCheck(true);
    try {
      const response = await fetch(`/api/admin/settings/tracker-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();
      if (data.success) {
        setTrackerCheckEnabled(enabled);
      } else {
        alert("Failed to update setting: " + data.error);
      }
    } catch (error) {
      console.error("Error updating tracker check setting:", error);
      alert("Failed to update setting");
    } finally {
      setUpdatingTrackerCheck(false);
    }
  };

  // Загрузка настройки режима подписки
  const loadSubscriptionSetting = async () => {
    try {
      const response = await fetch(`/api/admin/settings/subscription`, {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSubscriptionEnabled(data.enabled);
      }
    } catch (error) {
      console.error("Error loading subscription setting:", error);
    }
  };

  // Обновление настройки режима подписки
  const updateSubscriptionSetting = async (enabled) => {
    setUpdatingSubscription(true);
    try {
      const response = await fetch(`/api/admin/settings/subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();
      if (data.success) {
        setSubscriptionEnabled(enabled);
      } else {
        alert("Failed to update setting: " + data.error);
      }
    } catch (error) {
      console.error("Error updating subscription setting:", error);
      alert("Failed to update setting");
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const loadAutoAssignPasskeySetting = async () => {
    try {
      const response = await fetch(`/api/admin/settings/auto-assign-passkey`, {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setAutoAssignPasskey(data.enabled);
        setPasskeyFileConfigured(data.configured ?? null);
      }
    } catch (error) {
      console.error("Error loading auto-assign passkey setting:", error);
    }
  };

  const updateAutoAssignPasskeySetting = async (enabled) => {
    setUpdatingAutoAssignPasskey(true);
    try {
      const response = await fetch(`/api/admin/settings/auto-assign-passkey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();
      if (data.success) {
        setAutoAssignPasskey(enabled);
        setPasskeyFileConfigured(data.configured ?? null);
      } else {
        alert("Failed to update setting: " + data.error);
      }
    } catch (error) {
      console.error("Error updating auto-assign passkey setting:", error);
      alert("Failed to update setting");
    } finally {
      setUpdatingAutoAssignPasskey(false);
    }
  };

  const loadIpBlocks = async () => {
    setLoadingIpBlocks(true);
    try {
      const response = await fetch(`/api/admin/ip-blocks`, {
        headers: {
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setBlockedIps(data.blocks || []);
      } else {
        console.error("Error loading IP blocks:", data.error);
      }
    } catch (error) {
      console.error("Error loading IP blocks:", error);
    } finally {
      setLoadingIpBlocks(false);
    }
  };

  const handleAddIpBlock = async () => {
    if (!ipBlockValue.trim()) {
      alert("IP is required");
      return;
    }

    setUpdatingIpBlocks(true);
    try {
      const response = await fetch(`/api/admin/ip-blocks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({
          ip: ipBlockValue.trim(),
          reason: ipBlockReason.trim(),
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        alert(`Error: ${data.error || "Failed to block IP"}`);
        return;
      }

      setIpBlockValue("");
      setIpBlockReason("");
      await loadIpBlocks();
    } catch (error) {
      console.error("Error blocking IP:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setUpdatingIpBlocks(false);
    }
  };

  const handleRemoveIpBlock = async (ip) => {
    if (!window.confirm(`Remove block for IP ${ip}?`)) {
      return;
    }

    setUpdatingIpBlocks(true);
    try {
      const response = await fetch(`/api/admin/ip-blocks/unblock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({ ip }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        alert(`Error: ${data.error || "Failed to unblock IP"}`);
        return;
      }

      await loadIpBlocks();
    } catch (error) {
      console.error("Error unblocking IP:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setUpdatingIpBlocks(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-8 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="relative bg-gray-900 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">Admin Panel</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Вкладки */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-6 py-3 font-medium ${
              activeTab === "users"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Users className="inline mr-2" size={16} />
            Users
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`px-6 py-3 font-medium ${
              activeTab === "stats"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Database className="inline mr-2" size={16} />
            Statistics
          </button>
          <button
            onClick={() => setActiveTab("global-search")}
            className={`px-6 py-3 font-medium ${
              activeTab === "global-search"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Settings className="inline mr-2" size={16} />
            Global Search
          </button>
          <button
            onClick={() => setActiveTab("ip-blocks")}
            className={`px-6 py-3 font-medium ${
              activeTab === "ip-blocks"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Ban className="inline mr-2" size={16} />
            IP Blocks
          </button>
        </div>

        {/* Содержимое */}
        <div
          className={`p-6 max-h-[70vh] overflow-y-auto ${
            selectedUserIds.size > 0 && activeTab === "users" ? "pb-24" : ""
          }`}
        >
          {" "}
          {activeTab === "users" && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Users Management ({totalUsers} total)
              </h3>

              {/* Поиск и фильтры */}
              <div className="mb-4 space-y-3">
                {/* Поиск */}
                {(searchTerm || searchType) && (
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-300">Search:</span>
                      <span className="text-white">"{searchTerm}"</span>
                      <span className="text-gray-300">in</span>
                      <span className="text-blue-400">{searchType}</span>
                      <button
                        onClick={clearSearch}
                        className="ml-auto px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded text-white text-xs"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Фильтры */}
                {(accessFilter !== "all" ||
                  statusFilter !== "all" ||
                  gsTokensFilter !== "all" ||
                  passkeyFilter !== "all" ||
                  downloadsFilter !== "all") && (
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-gray-300">Active Filters:</span>
                      {accessFilter !== "all" && (
                        <span className="px-2 py-1 bg-green-600 rounded text-xs text-white">
                          Access:{" "}
                          {accessFilter === "granted_lifetime"
                            ? "Lifetime"
                            : accessFilter === "granted_subscription"
                              ? "Subscription"
                              : accessFilter === "expired"
                                ? "Expired"
                                : accessFilter === "granted"
                                  ? "Granted"
                                  : "Not Granted"}
                        </span>
                      )}
                      {statusFilter !== "all" && (
                        <span className="px-2 py-1 bg-blue-600 rounded text-xs text-white">
                          Status:{" "}
                          {statusFilter === "active" ? "Active" : "Banned"}
                        </span>
                      )}
                      {gsTokensFilter !== "all" && (
                        <span className="px-2 py-1 bg-purple-600 rounded text-xs text-white">
                          GS Tokens:{" "}
                          {gsTokensFilter === "used"
                            ? "Used"
                            : gsTokensFilter === "not_used"
                              ? "Not Used"
                              : "High Usage (≥100)"}
                        </span>
                      )}
                      {downloadsFilter !== "all" && (
                        <span className="px-2 py-1 bg-cyan-600 rounded text-xs text-white">
                          Downloads:{" "}
                          {downloadsFilter === "with"
                            ? "With downloads"
                            : downloadsFilter === "none"
                              ? "No downloads"
                              : downloadsFilter === "desc"
                                ? "Most to least"
                                : "Least to most"}
                        </span>
                      )}
                      {passkeyFilter !== "all" && (
                        <span
                          className="px-2 py-1 bg-orange-600 rounded text-xs text-white"
                          title={passkeyFilter}
                        >
                          PPasskey: {truncatePasskey(passkeyFilter)}
                        </span>
                      )}
                      <button
                        onClick={resetFilters}
                        className="ml-auto px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-xs"
                      >
                        Reset All Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-3 text-gray-300 w-10">
                        <input
                          ref={selectAllCheckboxRef}
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAllOnPage}
                          className="h-4 w-4 accent-blue-500 cursor-pointer"
                          title="Select all users on this page"
                        />
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex items-center gap-2">
                          <span
                            className="cursor-pointer hover:text-white"
                            onClick={() => {
                              const term = prompt("Search by Telegram ID:");
                              if (term) handleSearch(term, "telegram_id");
                            }}
                          >
                            Telegram ID 🔍
                          </span>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex items-center gap-2">
                          <span
                            className="cursor-pointer hover:text-white"
                            onClick={() => {
                              const term = prompt("Search by Username:");
                              if (term) handleSearch(term, "username");
                            }}
                          >
                            Username 🔍
                          </span>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex flex-col gap-1">
                          <span>Access</span>
                          <select
                            value={accessFilter}
                            onChange={(e) =>
                              handleFilterChange("access", e.target.value)
                            }
                            className="text-xs bg-gray-700 text-white rounded px-2 py-1 cursor-pointer hover:bg-gray-600"
                          >
                            <option value="all">All</option>
                            <option value="granted_lifetime">
                              Granted (Lifetime)
                            </option>
                            <option value="granted_subscription">
                              Granted (Subscription)
                            </option>
                            <option value="expired">Expired</option>
                            <option value="not_granted">Not Granted</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex flex-col gap-1">
                          <span>Status</span>
                          <select
                            value={statusFilter}
                            onChange={(e) =>
                              handleFilterChange("status", e.target.value)
                            }
                            className="text-xs bg-gray-700 text-white rounded px-2 py-1 cursor-pointer hover:bg-gray-600"
                          >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="banned">Banned</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex flex-col gap-1">
                          <span>Downloads</span>
                          <select
                            value={downloadsFilter}
                            onChange={(e) =>
                              handleFilterChange("downloads", e.target.value)
                            }
                            className="text-xs bg-gray-700 text-white rounded px-2 py-1 cursor-pointer hover:bg-gray-600"
                          >
                            <option value="all">All</option>
                            <option value="with">With downloads</option>
                            <option value="none">No downloads</option>
                            <option value="desc">Most to least</option>
                            <option value="asc">Least to most</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex flex-col gap-1">
                          <span>GS Tokens</span>
                          <select
                            value={gsTokensFilter}
                            onChange={(e) =>
                              handleFilterChange("gsTokens", e.target.value)
                            }
                            className="text-xs bg-gray-700 text-white rounded px-2 py-1 cursor-pointer hover:bg-gray-600"
                          >
                            <option value="all">All</option>
                            <option value="used">Used</option>
                            <option value="not_used">Not Used</option>
                            <option value="high_usage">High (≥100)</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex flex-col gap-1">
                          <span title="Personal Passkey">PPasskey</span>
                          <select
                            value={passkeyFilter}
                            onChange={(e) =>
                              handleFilterChange("passkey", e.target.value)
                            }
                            className="text-xs bg-gray-700 text-white rounded px-2 py-1 cursor-pointer hover:bg-gray-600 max-w-[170px]"
                          >
                            <option value="all">All</option>
                            {passkeyOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {truncatePasskey(option.value)} ({option.count})
                              </option>
                            ))}
                          </select>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        Last Login
                      </th>
                      <th className="text-left p-3 text-gray-300">
                        <div className="flex items-center gap-2">
                          <span
                            className="cursor-pointer hover:text-white"
                            onClick={() => {
                              const term = prompt("Search by IP:");
                              if (term) handleSearch(term, "ip");
                            }}
                          >
                            IPs 🔍
                          </span>
                        </div>
                      </th>
                      <th className="text-left p-3 text-gray-300">Joined</th>
                      <th className="text-left p-3 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.telegram_id}
                        className={`border-b border-gray-800 hover:bg-gray-800/50 ${
                          selectedUserIds.has(String(user.telegram_id))
                            ? "bg-blue-900/20"
                            : ""
                        }`}
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(
                              String(user.telegram_id),
                            )}
                            onChange={() =>
                              toggleUserSelection(user.telegram_id)
                            }
                            className="h-4 w-4 accent-blue-500 cursor-pointer"
                          />
                        </td>
                        <td className="p-3 text-white font-mono">
                          {user.telegram_id}
                        </td>
                        <td className="p-3 text-white">
                          <button
                            onClick={() => {
                              if (onOpenProfile) {
                                onOpenProfile(String(user.telegram_id));
                              }
                            }}
                            className="hover:text-orange-400 hover:underline transition-colors cursor-pointer text-left"
                            title="Open profile"
                          >
                            {user.username || "No username"}
                          </button>
                          {user.is_admin && (
                            <span className="ml-2 text-xs bg-red-600 px-2 py-1 rounded">
                              ADMIN
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {(() => {
                            const state = getAccessState(user);
                            if (state === "lifetime") {
                              return (
                                <span className="px-2 py-1 rounded text-xs bg-green-600 text-white">
                                  Lifetime
                                </span>
                              );
                            }
                            if (state === "subscription") {
                              return (
                                <span
                                  className="px-2 py-1 rounded text-xs bg-blue-600 text-white"
                                  title={user.premium_plan_id || ""}
                                >
                                  {user.premium_plan_id
                                    ? `${user.premium_plan_id} `
                                    : "Sub "}
                                  до {formatShortDate(user.premium_expires_at)}
                                </span>
                              );
                            }
                            if (state === "expired") {
                              return (
                                <span className="px-2 py-1 rounded text-xs bg-yellow-600 text-yellow-50">
                                  Expired{" "}
                                  {formatShortDate(user.premium_expires_at)}
                                </span>
                              );
                            }
                            return (
                              <span className="px-2 py-1 rounded text-xs bg-gray-600 text-gray-300">
                                Denied
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              user.is_banned
                                ? "bg-red-600 text-white"
                                : "bg-green-600 text-white"
                            }`}
                          >
                            {user.is_banned ? "Banned" : "Active"}
                          </span>
                        </td>
                        <td className="p-3 text-white">
                          {user.dl_torrents_count}
                        </td>
                        <td className="p-3 text-blue-400 font-bold">
                          {user.gs_tokens_used || 0}
                        </td>
                        <td className="p-3 text-gray-300 text-xs font-mono">
                          {user.torrent_passkey ? (
                            <span title={user.torrent_passkey}>
                              {truncatePasskey(user.torrent_passkey)}
                            </span>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="p-3 text-gray-300 text-xs">
                          {user.last_login_at
                            ? formatDate(user.last_login_at)
                            : "Never"}
                        </td>
                        <td className="p-3">
                          {user.login_ips &&
                          Array.isArray(user.login_ips) &&
                          user.login_ips.length > 0 ? (
                            <button
                              onClick={() =>
                                setSelectedUserIps({
                                  username: user.username || user.telegram_id,
                                  ips: user.login_ips,
                                })
                              }
                              className="p-1 bg-indigo-600 hover:bg-indigo-700 rounded"
                              title="View IPs"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                              </svg>
                              <span className="ml-1 text-xs">
                                {user.login_ips.length}
                              </span>
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              No IPs
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-gray-300">
                          {formatDate(user.joined_at)}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openGrantModal(user)}
                              className="p-1 bg-green-600 hover:bg-green-700 rounded"
                              title="Grant Access"
                            >
                              <Check size={14} />
                            </button>
                            {user.access_granted && (
                              <button
                                onClick={() =>
                                  handleUserAction(
                                    "refund-stars",
                                    user.telegram_id,
                                  )
                                }
                                className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded text-yellow-50"
                                title="Refund Latest Stars Payment"
                              >
                                <Undo size={14} />
                              </button>
                            )}
                            {!user.is_banned ? (
                              <button
                                onClick={() =>
                                  handleUserAction("ban", user.telegram_id, {
                                    reason: "Manual ban",
                                  })
                                }
                                className="p-1 bg-red-600 hover:bg-red-700 rounded"
                                title="Ban User"
                              >
                                <Ban size={14} />
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleUserAction("unban", user.telegram_id)
                                }
                                className="p-1 bg-yellow-600 hover:bg-yellow-700 rounded"
                                title="Unban User"
                              >
                                <Check size={14} />
                              </button>
                            )}
                            {!user.is_admin ? (
                              <button
                                onClick={() =>
                                  handleUserAction("promote", user.telegram_id)
                                }
                                className="p-1 bg-purple-600 hover:bg-purple-700 rounded"
                                title="Make Admin"
                              >
                                <Crown size={14} />
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleUserAction("demote", user.telegram_id)
                                }
                                className="p-1 bg-gray-600 hover:bg-gray-700 rounded"
                                title="Remove Admin"
                              >
                                <UserMinus size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setNoteText(user.note || "");
                                setPasskeyText(user.torrent_passkey || "");
                              }}
                              className="p-1 bg-blue-600 hover:bg-blue-700 rounded"
                              title="Edit User"
                            >
                              <Edit3 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Пагинация */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-700 pt-4">
                  <div className="text-sm text-gray-400">
                    Showing {(currentPage - 1) * 100 + 1} -{" "}
                    {Math.min(currentPage * 100, totalUsers)} of {totalUsers}{" "}
                    users
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Кнопка "В начало" */}
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === 1
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-700 text-white hover:bg-gray-600"
                      }`}
                    >
                      ««
                    </button>

                    {/* Кнопка "Назад" */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === 1
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-700 text-white hover:bg-gray-600"
                      }`}
                    >
                      ‹ Prev
                    </button>

                    {/* Номера страниц */}
                    <div className="flex items-center gap-1">
                      {(() => {
                        const pages = [];
                        let startPage = Math.max(1, currentPage - 2);
                        let endPage = Math.min(totalPages, currentPage + 2);

                        // Показываем первую страницу и многоточие если нужно
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => handlePageChange(1)}
                              className="w-10 h-10 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600"
                            >
                              1
                            </button>,
                          );
                          if (startPage > 2) {
                            pages.push(
                              <span key="dots1" className="px-2 text-gray-500">
                                ...
                              </span>,
                            );
                          }
                        }

                        // Страницы вокруг текущей
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                                i === currentPage
                                  ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                  : "bg-gray-700 text-white hover:bg-gray-600"
                              }`}
                            >
                              {i}
                            </button>,
                          );
                        }

                        // Многоточие и последняя страница
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="dots2" className="px-2 text-gray-500">
                                ...
                              </span>,
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => handlePageChange(totalPages)}
                              className="w-10 h-10 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600"
                            >
                              {totalPages}
                            </button>,
                          );
                        }

                        return pages;
                      })()}
                    </div>

                    {/* Кнопка "Вперёд" */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === totalPages
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-700 text-white hover:bg-gray-600"
                      }`}
                    >
                      Next ›
                    </button>

                    {/* Кнопка "В конец" */}
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === totalPages
                          ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                          : "bg-gray-700 text-white hover:bg-gray-600"
                      }`}
                    >
                      »»
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === "stats" && stats && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">
                Statistics
              </h3>{" "}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-2">Users</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total:</span>
                      <span className="text-white font-bold">
                        {stats.users.total_users}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">With Access:</span>
                      <span className="text-green-400 font-bold">
                        {stats.users.users_with_access}
                      </span>
                    </div>
                    {stats.users.users_lifetime !== undefined && (
                      <>
                        <div className="flex justify-between pl-3">
                          <span className="text-gray-400 text-sm">
                            ↳ Lifetime:
                          </span>
                          <span className="text-green-300 font-bold text-sm">
                            {stats.users.users_lifetime}
                          </span>
                        </div>
                        <div className="flex justify-between pl-3">
                          <span className="text-gray-400 text-sm">
                            ↳ Subscription:
                          </span>
                          <span className="text-blue-300 font-bold text-sm">
                            {stats.users.users_subscription}
                          </span>
                        </div>
                        <div className="flex justify-between pl-3">
                          <span className="text-gray-400 text-sm">
                            ↳ Expired:
                          </span>
                          <span className="text-yellow-300 font-bold text-sm">
                            {stats.users.users_expired}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-300">Banned:</span>
                      <span className="text-red-400 font-bold">
                        {stats.users.banned_users}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Admins:</span>
                      <span className="text-blue-400 font-bold">
                        {stats.users.admin_users}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-2">
                    Downloads
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total:</span>
                      <span className="text-white font-bold">
                        {stats.users.total_downloads || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-2">
                    Content
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Torrents:</span>
                      <span className="text-white font-bold">
                        {stats.torrents.total_torrents}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Categories:</span>
                      <span className="text-white font-bold">
                        {stats.torrents.categories_count}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Новая секция статистики покупок */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-2">
                    Purchases
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total:</span>
                      <span className="text-green-400 font-bold">
                        {stats.payments?.total_purchases || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Unique Buyers:</span>
                      <span className="text-blue-400 font-bold">
                        {stats.payments?.unique_buyers || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">USDT:</span>
                      <span className="text-yellow-400 font-bold">
                        {stats.payments?.usdt_purchases || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Stars:</span>
                      <span className="text-purple-400 font-bold">
                        {stats.payments?.stars_purchases || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "global-search" && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Global Search Settings
              </h3>

              <div className="space-y-6">
                {/* Переключатель видимости Global Search */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">
                        Global Search Visibility
                      </h4>
                      <p className="text-sm text-gray-400">
                        {globalSearchEnabled
                          ? "Global Search is visible and available for users with access"
                          : "Global Search is hidden from all users (except admins)"}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        updateGlobalSearchSetting(!globalSearchEnabled)
                      }
                      disabled={updatingGlobalSearch}
                      className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
                        globalSearchEnabled ? "bg-green-600" : "bg-gray-600"
                      } ${
                        updatingGlobalSearch
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          globalSearchEnabled
                            ? "translate-x-9"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-300">
                      <strong>Note:</strong> When enabled, Global Search toggle
                      is visible in the app. Users with granted access can use
                      it. Users without access will see payment modal. Admins
                      always have access regardless of this setting.
                    </p>
                  </div>
                </div>

                {/* Переключатель проверки трекеров */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">
                        Tracker Verification
                      </h4>
                      <p className="text-sm text-gray-400">
                        {trackerCheckEnabled
                          ? "Trackers are verified before replacement (slower but more reliable)"
                          : "First tracker from list is used without verification (faster)"}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        updateTrackerCheckSetting(!trackerCheckEnabled)
                      }
                      disabled={updatingTrackerCheck}
                      className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
                        trackerCheckEnabled ? "bg-green-600" : "bg-gray-600"
                      } ${
                        updatingTrackerCheck
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          trackerCheckEnabled
                            ? "translate-x-9"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-300">
                      <strong>When ON:</strong> Each tracker from trackers.txt
                      is checked for availability before replacement. More
                      reliable but slower.
                    </p>
                    <p className="text-xs text-gray-300 mt-2">
                      <strong>When OFF:</strong> First tracker from trackers.txt
                      is used directly without checking. Faster but may fail if
                      tracker is down.
                    </p>
                  </div>
                </div>

                {/* Переключатель режима оплаты: Subscription / Lifetime */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">
                        Subscription Billing
                      </h4>
                      <p className="text-sm text-gray-400">
                        {subscriptionEnabled
                          ? "Subscription plans popup is active (month / quarter / half-year / year)"
                          : "Lifetime one-time payment popup is active (STARS_PRICE)"}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        updateSubscriptionSetting(!subscriptionEnabled)
                      }
                      disabled={updatingSubscription}
                      className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
                        subscriptionEnabled ? "bg-green-600" : "bg-gray-600"
                      } ${
                        updatingSubscription
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          subscriptionEnabled
                            ? "translate-x-9"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-300">
                      <strong>When ON:</strong> Users see the subscription
                      plans carousel. Prices from STARS_PRICE_MONTH / QUARTER /
                      HALF_YEAR / YEAR. Access expires after the selected
                      period.
                    </p>
                    <p className="text-xs text-gray-300 mt-2">
                      <strong>When OFF:</strong> Users see the classic lifetime
                      popup (one-time payment). Price from STARS_PRICE. Access
                      is granted forever (premium_expires_at = NULL).
                    </p>
                  </div>
                </div>

                {/* Авто-назначение personal passkey */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">
                        Auto-Assign Personal Passkey
                      </h4>
                      <p className="text-sm text-gray-400">
                        {autoAssignPasskey
                          ? "New paying users get the current tracker from personal_passkey.txt"
                          : "Personal passkeys are not assigned automatically"}
                      </p>
                      {passkeyFileConfigured === false && (
                        <p className="text-xs text-yellow-500 mt-1">
                          personal_passkey.txt is empty or missing
                        </p>
                      )}
                      {passkeyFileConfigured === true && (
                        <p className="text-xs text-gray-500 mt-1">
                          Tracker file is configured
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        updateAutoAssignPasskeySetting(!autoAssignPasskey)
                      }
                      disabled={updatingAutoAssignPasskey}
                      className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
                        autoAssignPasskey ? "bg-green-600" : "bg-gray-600"
                      } ${
                        updatingAutoAssignPasskey
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          autoAssignPasskey
                            ? "translate-x-9"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-300">
                      <strong>When ON:</strong> After a successful purchase
                      (subscription or lifetime), the current tracker from{" "}
                      <code className="text-orange-300">personal_passkey.txt</code>{" "}
                      is written to the user&apos;s Personal Passkey field — same
                      as manual Edit User. The file is never modified; edit it
                      yourself when you want to change the tracker.
                    </p>
                    <p className="text-xs text-gray-300 mt-2">
                      <strong>When OFF:</strong> No personal passkey is assigned
                      on purchase. Existing behavior stays unchanged.
                    </p>
                    <p className="text-xs text-gray-300 mt-2">
                      Users who already have a Personal Passkey are never
                      overwritten. If the file is empty, payment still succeeds
                      and access is granted without a passkey.
                    </p>
                  </div>
                </div>

                {/* Статус токена */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-white">
                      API Token Status
                    </h4>
                    <button
                      onClick={checkTokenInfo}
                      disabled={checkingToken}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      {checkingToken ? "Checking..." : "Check Token"}
                    </button>
                  </div>

                  {tokenInfo && (
                    <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                      {tokenInfo.error ? (
                        <div className="text-red-400">
                          <strong>Error:</strong> {tokenInfo.error}
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-300">
                              User requests left:
                            </span>
                            <span className="text-green-400 font-bold">
                              {tokenInfo.user_requests_left ??
                                tokenInfo.requests_left ??
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">
                              Token requests left:
                            </span>
                            <span className="text-blue-400 font-bold">
                              {tokenInfo.token_requests_left ??
                                tokenInfo.total_requests ??
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Token ID:</span>
                            <span className="text-gray-300 font-mono text-xs">
                              {tokenInfo.token_id || "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Status:</span>
                            <span
                              className={`font-bold ${
                                tokenInfo.status === "Active"
                                  ? "text-green-400"
                                  : "text-red-400"
                              }`}
                            >
                              {tokenInfo.status || "N/A"}
                            </span>
                          </div>
                          {tokenInfo.status === "Exhausted" && (
                            <div className="mt-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                              <p className="text-red-400 text-sm font-semibold">
                                Token Exhausted
                              </p>
                              <p className="text-red-300 text-xs mt-1">
                                Issue a new API token in the ExFreedomist
                                account at download.exfreedomist.com
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Информация о трекерах */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-4">
                    Supported Trackers
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-gray-300">RuTracker</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                      <span className="text-gray-300">NNM-Club</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-300">PirateBay</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-300">1337x</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="text-gray-300">RARBG</span>
                    </div>
                  </div>
                </div>

                {/* Инструкции */}
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-4">
                    Setup Instructions
                  </h4>
                  <div className="space-y-3 text-sm text-gray-300">
                    <div>
                      <strong className="text-white">1. Get API Token:</strong>
                      <p>
                        Register at{" "}
                        <a
                          href="https://download.exfreedomist.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          download.exfreedomist.com
                        </a>
                        , wait for API access, then create a token in{" "}
                        <strong>API tokens</strong>.
                      </p>
                    </div>
                    <div>
                      <strong className="text-white">2. Update .env:</strong>
                      <p>
                        Set{" "}
                        <code className="bg-gray-700 px-2 py-1 rounded">
                          EXFREEDOMIST_API_TOKEN
                        </code>{" "}
                        in your .env file
                      </p>
                    </div>
                    <div>
                      <strong className="text-white">3. Restart Server:</strong>
                      <p>
                        <code className="bg-gray-700 px-2 py-1 rounded">
                          pm2 restart cgpeers-server
                        </code>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "ip-blocks" && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                IP Access Blocking
              </h3>

              <div className="space-y-6">
                <div className="bg-gray-800 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-white mb-2">
                    Block IP From App
                  </h4>
                  <p className="text-sm text-gray-400 mb-4">
                    These IPs will receive a generic 404 before the app, static
                    files, or API responses are served.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={ipBlockValue}
                      onChange={(e) => setIpBlockValue(e.target.value)}
                      placeholder="Enter exact IP, for example 193.17.95.195"
                      className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={ipBlockReason}
                      onChange={(e) => setIpBlockReason(e.target.value)}
                      placeholder="Reason, for example abuse or bypass attempt"
                      className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-gray-400">
                      Tip: copy the IP from the user IP list in the Users tab.
                    </p>
                    <button
                      onClick={handleAddIpBlock}
                      disabled={updatingIpBlocks || !ipBlockValue.trim()}
                      className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      {updatingIpBlocks ? "Blocking..." : "Block IP"}
                    </button>
                  </div>
                </div>

                <div className="bg-gray-800 p-6 rounded-lg">
                  <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                    <div>
                      <h4 className="text-lg font-medium text-white">
                        Current App Blocks
                      </h4>
                      <p className="text-sm text-gray-400">
                        Only manually added permanent IP blocks are shown here.
                      </p>
                    </div>
                    <button
                      onClick={loadIpBlocks}
                      disabled={loadingIpBlocks || updatingIpBlocks}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      {loadingIpBlocks ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>

                  {blockedIps.length === 0 ? (
                    <div className="text-sm text-gray-400 bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                      No IPs are currently blocked at the app level.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {blockedIps.map((entry) => (
                        <div
                          key={entry.ip}
                          className="flex items-start justify-between gap-4 bg-gray-900/50 border border-gray-700 rounded-lg p-4"
                        >
                          <div className="min-w-0">
                            <div className="text-white font-mono break-all">
                              {entry.ip}
                            </div>
                            <div className="text-sm text-gray-300 mt-1">
                              {entry.reason || "Manual block"}
                            </div>
                            <div className="text-xs text-gray-500 mt-2">
                              Blocked:{" "}
                              {entry.blockedAt
                                ? formatDate(entry.blockedAt)
                                : "Unknown"}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveIpBlock(entry.ip)}
                            disabled={updatingIpBlocks}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors whitespace-nowrap"
                          >
                            Unblock
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {selectedUserIds.size > 0 && activeTab === "users" && (
          <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
            <div className="flex items-center gap-3 rounded-xl border border-gray-600 bg-gray-800/95 px-4 py-3 shadow-2xl backdrop-blur-sm">
              <span className="whitespace-nowrap text-sm text-gray-300">
                {selectedUserIds.size} selected
              </span>
              <button
                type="button"
                onClick={() => setShowBulkPasskeyModal(true)}
                className="flex items-center gap-2 rounded-lg bg-orange-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <title>passkey</title>
                  <path
                    fill="currentColor"
                    d="M4 19v-1.646q0-.62.36-1.159q.361-.54.97-.837q1.416-.68 2.834-1.018Q9.58 14 11 14q.553 0 1.106.057q.553.056 1.106.17q.015 1.123.515 2.103T15.1 18v1zm14.692 2.385l-1.167-1.157V16.64q-.856-.25-1.392-.961q-.537-.71-.537-1.622q0-1.124.794-1.918t1.918-.794t1.908.795t.784 1.92q0 .872-.484 1.535q-.483.663-1.247.962l.962.961l-1.154 1.16l1.154 1.16zM11 11.769q-1.237 0-2.119-.881T8 8.769t.881-2.119T11 5.77t2.119.88T14 8.77t-.881 2.118t-2.119.881m7.847 2.434q.23-.24.23-.55q0-.309-.23-.539t-.54-.23t-.548.23t-.24.54t.24.549t.549.24t.539-.24"
                  />
                </svg>
                Change Personal Passkey
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно редактирования пользователя */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-60">
          <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">
              Edit User: {selectedUser.username || selectedUser.telegram_id}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Note:</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">
                  Personal Passkey:
                </label>
                <input
                  type="text"
                  value={passkeyText}
                  onChange={(e) => setPasskeyText(e.target.value)}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                  placeholder="http://tracker.cgpeers.to:420/key/announce"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  handleUserAction("note", selectedUser.telegram_id, {
                    note: noteText,
                  });
                  handleUserAction("passkey", selectedUser.telegram_id, {
                    passkey: passkeyText,
                  });
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                Save
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkPasskeyModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90">
          <div className="mx-4 w-full max-w-md rounded-lg bg-gray-800 p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                Change Personal Passkey
              </h3>
              <button
                type="button"
                onClick={() => setShowBulkPasskeyModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <p className="mb-4 text-sm text-gray-400">
              This passkey will be assigned to{" "}
              <span className="font-medium text-white">
                {selectedUserIds.size}
              </span>{" "}
              selected user{selectedUserIds.size === 1 ? "" : "s"}.
            </p>

            <label className="mb-2 block text-gray-300">
              Personal Passkey
            </label>
            <input
              type="text"
              value={bulkPasskeyText}
              onChange={(e) => setBulkPasskeyText(e.target.value)}
              className="w-full rounded bg-gray-700 p-2 text-white"
              placeholder="https://tracker.cgpeers.to/.../announce"
            />

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => handleBulkPasskeySubmit()}
                disabled={bulkPasskeySubmitting || !bulkPasskeyText.trim()}
                className="rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 disabled:bg-gray-600"
              >
                {bulkPasskeySubmitting ? "Saving..." : "Apply"}
              </button>
              <button
                type="button"
                onClick={() => handleBulkPasskeySubmit({ clear: true })}
                disabled={bulkPasskeySubmitting}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:bg-gray-600"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowBulkPasskeyModal(false)}
                className="rounded bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно выдачи доступа (Grant Access) */}
      {grantModalUser && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70]">
          <div className="bg-gray-800 p-6 rounded-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Grant Access
              </h3>
              <button
                onClick={() => setGrantModalUser(null)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              User:{" "}
              <span className="text-white font-medium">
                {grantModalUser.username || grantModalUser.telegram_id}
              </span>
            </p>

            <label className="block text-xs text-gray-400 mb-1">Duration</label>
            <select
              value={grantDuration}
              onChange={(e) => setGrantDuration(e.target.value)}
              className="w-full mb-3 bg-gray-700 text-white rounded px-3 py-2 text-sm"
            >
              <option value="lifetime">Lifetime (бессрочно)</option>
              <option value="30">30 days (1 month)</option>
              <option value="90">90 days (3 months)</option>
              <option value="180">180 days (6 months)</option>
              <option value="365">365 days (1 year)</option>
              <option value="custom">Custom (days)</option>
            </select>

            {grantDuration === "custom" && (
              <input
                type="number"
                min="1"
                value={grantCustomDays}
                onChange={(e) => setGrantCustomDays(e.target.value)}
                placeholder="Number of days"
                className="w-full mb-3 bg-gray-700 text-white rounded px-3 py-2 text-sm"
              />
            )}

            <label className="block text-xs text-gray-400 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={grantNote}
              onChange={(e) => setGrantNote(e.target.value)}
              placeholder="Reason / comment"
              className="w-full mb-4 bg-gray-700 text-white rounded px-3 py-2 text-sm"
            />

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setGrantModalUser(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitGrantAccess}
                disabled={grantSubmitting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded text-white text-sm"
              >
                {grantSubmitting ? "Granting..." : "Grant Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для отображения IP адресов */}
      {selectedUserIps && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-60">
          <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                Login IPs: {selectedUserIps.username}
              </h3>
              <button
                onClick={() => setSelectedUserIps(null)}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {selectedUserIps.ips.map((ip, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-700 rounded-lg font-mono text-sm text-white flex items-center justify-between"
                >
                  <span>{ip}</span>
                  <span className="text-xs text-gray-400">#{index + 1}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-400">
                Total IP addresses: {selectedUserIps.ips.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
